(() => {
  "use strict";

  const ROOT_SELECTOR = "[data-ubw-fgc]";
  const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

  const ratioFormatter = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const speedFormatter = new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  });

  const gcd = (a, b) => (b ? gcd(b, a % b) : Math.abs(a));

  function clampInteger(value, minimum, maximum) {
    const number = Math.round(Number(value));

    if (!Number.isFinite(number)) {
      return minimum;
    }

    return Math.max(minimum, Math.min(maximum, number));
  }

  function calculateGear(chainring, cog, bothLegs) {
    const front = clampInteger(chainring, 30, 60);
    const rear = clampInteger(cog, 12, 24);
    const divisor = gcd(front, rear);
    const reducedFront = front / divisor;
    const basePatches = rear / divisor;

    const patches =
      bothLegs && reducedFront % 2 === 1
        ? basePatches * 2
        : basePatches;

    return {
      chainring: front,
      cog: rear,
      ratio: front / rear,
      basePatches,
      patches,
      doubled: bothLegs && patches !== basePatches
    };
  }

  function calculateSpeed(ratio, cadence, circumferenceMillimetres) {
    return (
      ratio *
      Number(cadence) *
      Number(circumferenceMillimetres) *
      60 /
      1000000
    );
  }

  function speedMarkerPosition(speed, maximum = 60) {
    return Math.max(
      1.5,
      Math.min(98.5, Number(speed) / maximum * 100)
    );
  }

  function wearProfile(patches) {
    if (patches >= 15) {
      return {
        key: "good",
        badge: "VERY GOOD DISTRIBUTION",
        title: `The tyre wears at ${patches} points`,
        copy:
          "Wear is spread across many parts of the tyre. This is considerably better than a combination that always locks the wheel at the same point."
      };
    }

    if (patches >= 8) {
      return {
        key: "good",
        badge: "GOOD DISTRIBUTION",
        title: `The tyre wears at ${patches} points`,
        copy:
          "Wear is distributed relatively evenly across the tyre. A good result for a fixed gear that is skidded regularly."
      };
    }

    if (patches >= 4) {
      return {
        key: "warn",
        badge: "LIMITED DISTRIBUTION",
        title: `The tyre wears at ${patches} points`,
        copy:
          "Wear is concentrated on fewer points. Check the tyre frequently if you skid often."
      };
    }

    return {
      key: "bad",
      badge:
        patches === 1
          ? "ONE WEAR POINT"
          : "HIGHLY CONCENTRATED WEAR",
      title:
        patches === 1
          ? "Every skid hits the same point"
          : `Only ${patches} skid patches`,
      copy:
        "The tyre wears quickly in the same area. A nearby ratio with more skid patches is normally a better choice."
    };
  }

  function initCalculator(root) {
    if (
      !root ||
      root.dataset.ubwFgcInitialized === "true"
    ) {
      return;
    }

    const find = (role) =>
      root.querySelector(`[data-role="${role}"]`);

    const elements = {
      chainring: find("chainring"),
      cog: find("cog"),
      chainringRange: find("chainring-range"),
      cogRange: find("cog-range"),
      bothLegs: find("both-legs"),
      cadence: find("cadence"),
      tireSize: find("tire-size"),
      ratio: find("ratio"),
      ratioFormula: find("ratio-formula"),
      patches: find("patches"),
      legMode: find("leg-mode"),
      wheelCount: find("wheel-count"),
      wheelDescription: find("wheel-desc"),
      patchDots: find("patch-dots"),
      wearBadge: find("wear-badge"),
      wearTitle: find("wear-title"),
      wearCopy: find("wear-copy"),
      speedValue: find("speed-value"),
      speedMarker: find("speed-marker"),
      speedMarkerLabel: find("speed-marker-label"),
      speedCopy: find("speed-copy"),
      reset: root.querySelector('[data-action="reset"]')
    };

    if (
      Object.values(elements).some(
        (element) => !element
      )
    ) {
      return;
    }

    const presetButtons = Array.from(
      root.querySelectorAll("[data-preset]")
    );

    const stepButtons = Array.from(
      root.querySelectorAll("[data-step-target]")
    );

    function setValue(input, value) {
      const minimum = Number(input.min);
      const maximum = Number(input.max);

      input.value = Math.max(
        minimum,
        Math.min(
          maximum,
          Math.round(Number(value) || minimum)
        )
      );
    }

    function renderWheel(count) {
      elements.patchDots.replaceChildren();

      const radius = 98;

      for (
        let index = 0;
        index < count;
        index += 1
      ) {
        const angle =
          (-90 + 360 / count * index) *
          Math.PI /
          180;

        const circle = document.createElementNS(
          SVG_NAMESPACE,
          "circle"
        );

        circle.setAttribute(
          "class",
          "ubw-fgc__patch-dot"
        );

        circle.setAttribute(
          "cx",
          (
            120 +
            Math.cos(angle) * radius
          ).toFixed(2)
        );

        circle.setAttribute(
          "cy",
          (
            120 +
            Math.sin(angle) * radius
          ).toFixed(2)
        );

        circle.setAttribute(
          "r",
          count > 30 ? "3.2" : "4.2"
        );

        elements.patchDots.append(circle);
      }

      elements.wheelDescription.textContent =
        `${count} evenly distributed wear points`;
    }

    function updatePresets(selectedGear) {
      presetButtons.forEach((button) => {
        const [front, rear] =
          button.dataset.preset
            .split("-")
            .map(Number);

        const presetGear = calculateGear(
          front,
          rear,
          elements.bothLegs.checked
        );

        const label =
          button.querySelector("small");

        button.classList.toggle(
          "is-active",
          front === selectedGear.chainring &&
            rear === selectedGear.cog
        );

        if (label) {
          label.textContent =
            `${ratioFormatter.format(
              presetGear.ratio
            )} · ${presetGear.patches} ${
              presetGear.patches === 1
                ? "patch"
                : "patches"
            }`;
        }
      });
    }

    function render() {
      setValue(
        elements.chainring,
        elements.chainring.value
      );

      setValue(
        elements.cog,
        elements.cog.value
      );

      elements.chainringRange.value =
        elements.chainring.value;

      elements.cogRange.value =
        elements.cog.value;

      const gear = calculateGear(
        elements.chainring.value,
        elements.cog.value,
        elements.bothLegs.checked
      );

      const wear = wearProfile(
        gear.patches
      );

      const speed = calculateSpeed(
        gear.ratio,
        elements.cadence.value,
        elements.tireSize.value
      );

      const tyreLabel =
        elements.tireSize.options[
          elements.tireSize.selectedIndex
        ].text;

      elements.ratio.textContent =
        ratioFormatter.format(gear.ratio);

      elements.ratioFormula.textContent =
        `${gear.chainring} ÷ ${gear.cog}`;

      elements.patches.textContent =
        gear.patches;

      elements.wheelCount.textContent =
        gear.patches;

      elements.legMode.textContent =
        elements.bothLegs.checked
          ? gear.doubled
            ? "either foot forward doubles the number"
            : "same number with either foot forward"
          : "with the same foot forward";

      elements.wearBadge.textContent =
        wear.badge;

      elements.wearBadge.className =
        `ubw-fgc__wear-badge is-${wear.key}`;

      elements.wearTitle.textContent =
        wear.title;

      elements.wearCopy.textContent =
        wear.copy;

      const formattedSpeed =
        speedFormatter.format(speed);

      elements.speedValue.textContent =
        `${formattedSpeed} km/h`;

      elements.speedMarkerLabel.textContent =
        formattedSpeed;

      elements.speedMarker.style.left =
        `${speedMarkerPosition(speed)}%`;

      elements.speedCopy.textContent =
        `${gear.chainring} × ${gear.cog} · ` +
        `${elements.cadence.value} rpm · ` +
        tyreLabel;

      renderWheel(gear.patches);
      updatePresets(gear);
    }

    stepButtons.forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          const input =
            button.dataset.stepTarget ===
            "chainring"
              ? elements.chainring
              : elements.cog;

          setValue(
            input,
            Number(input.value) +
              Number(
                button.dataset.stepDelta
              )
          );

          render();
        }
      );
    });

    elements.chainring.addEventListener(
      "input",
      render
    );

    elements.cog.addEventListener(
      "input",
      render
    );

    elements.chainringRange.addEventListener(
      "input",
      () => {
        elements.chainring.value =
          elements.chainringRange.value;

        render();
      }
    );

    elements.cogRange.addEventListener(
      "input",
      () => {
        elements.cog.value =
          elements.cogRange.value;

        render();
      }
    );

    elements.bothLegs.addEventListener(
      "change",
      render
    );

    elements.cadence.addEventListener(
      "change",
      render
    );

    elements.tireSize.addEventListener(
      "change",
      render
    );

    presetButtons.forEach((button) => {
      button.addEventListener(
        "click",
        () => {
          const [front, rear] =
            button.dataset.preset.split("-");

          elements.chainring.value = front;
          elements.cog.value = rear;

          render();
        }
      );
    });

    elements.reset.addEventListener(
      "click",
      () => {
        elements.chainring.value = 48;
        elements.cog.value = 17;
        elements.bothLegs.checked = false;
        elements.cadence.value = "90";
        elements.tireSize.value = "2136";

        render();
      }
    );

    root.dataset.ubwFgcInitialized = "true";

    render();
  }

  function initAll(scope = document) {
    if (
      scope.matches &&
      scope.matches(ROOT_SELECTOR)
    ) {
      initCalculator(scope);
    }

    scope
      .querySelectorAll(ROOT_SELECTOR)
      .forEach(initCalculator);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => initAll()
    );
  } else {
    initAll();
  }

  if (!window.__ubwFgcSectionListener) {
    window.__ubwFgcSectionListener = true;

    document.addEventListener(
      "shopify:section:load",
      (event) => initAll(event.target)
    );
  }
})();